import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Clock } from 'lucide-react';

export const ProductCard = ({ product, index = 0, featured = false, cardSize = 'small', showPrice = true, showCategory = true }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const inStock = product.in_stock !== false;

  const getAspectRatio = () => {
    switch (cardSize) {
      case 'large': return 'aspect-square';
      case 'medium': return 'aspect-[4/5]';
      case 'small':
      default: return 'aspect-[3/4]';
    }
  };

  const getCategoryName = (category) => {
    const names = {
      'ru': {
        'rings': 'Помолвочные кольца',
        'earrings': 'Серьги-пусеты'
      },
      'lv': {
        'rings': 'Saderināšanās gredzeni',
        'earrings': 'Auskari'
      }
    };
    return names[lang]?.[category] || category;
  };

  const stockLabel = inStock 
    ? { ru: 'В наличии', lv: 'Noliktavā' }
    : { ru: 'Под заказ', lv: 'Pēc pasūtījuma' };

  return (
    <div
      className="group relative overflow-hidden bg-background rounded-sm shadow-sm hover:shadow-lg transition-shadow duration-300"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className={`relative overflow-hidden ${getAspectRatio()} bg-secondary`}>
          <img
            src={product.images[0]}
            alt={product.name[lang] || product.name.ru}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="eager"
            data-testid={`product-image-${product.id}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Бейдж наличия */}
          <div className={`absolute top-3 left-3 px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
            inStock 
              ? 'bg-green-600 text-white' 
              : 'bg-amber-600 text-white'
          }`}>
            {inStock ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {stockLabel[lang]}
          </div>
        </div>

        <div className="p-4 space-y-2">
          <h3 className="font-heading text-base md:text-lg tracking-tight group-hover:text-accent transition-colors duration-300 line-clamp-2" data-testid={`product-name-${product.id}`}>
            {product.name[lang] || product.name.ru}
          </h3>
          {showCategory && (
            <p className="font-body text-xs text-muted-foreground uppercase tracking-wider">
              {getCategoryName(product.category)}
            </p>
          )}
          {showPrice && (
            <div className="space-y-1">
              {product.original_price && product.original_price > product.price && (
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-0.5 bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider rounded">
                    {lang === 'lv' ? 'Akcija' : 'Акция'}
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-2">
                <p className="font-body text-lg font-semibold text-accent" data-testid={`product-price-${product.id}`}>
                  €{product.price.toLocaleString(lang === 'lv' ? 'lv-LV' : 'ru-RU')}
                </p>
                {product.original_price && product.original_price > product.price && (
                  <p className="font-body text-sm text-muted-foreground line-through">
                    €{product.original_price.toLocaleString(lang === 'lv' ? 'lv-LV' : 'ru-RU')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};
